// 앱 진입점: 라우팅과 관리자 로그인
import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import App from './App';
import { auth, verifyAdminPassword } from './firebase';
import { signInWithCustomToken, setPersistence, browserSessionPersistence } from 'firebase/auth';
import ProtectedRoute from './ProtectedRoute';
import './index.css';
import GaRouterTracker from './hooks/useGaPageview';
import { generateDeviceId } from './utils/deviceUtils';

// 메인 앱 외 페이지는 방문 시에만 로드해 초기 번들을 줄인다.
const AdminDashboard = lazy(() => import('./AdminDashboard'));
const SharePromotionPage = lazy(() => import('./pages/SharePromotionPage'));
const IntroPage = lazy(() => import('./pages/IntroPage'));
const WorkDiaryPage = lazy(() => import('./pages/WorkDiaryPage'));
const CustomerReferralTreePage = lazy(() => import('./pages/CustomerReferralTreePage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));

const MainApp = () => {
  return (
    <Router>
      <GaRouterTracker />
      <Suspense fallback={null}>
      <Routes>
        {/* 메인 앱 - 사용자용 */}
        <Route path="/" element={<App />} />

        {/* 공유 프로모션 페이지 */}
        <Route path="/share-promotion" element={<SharePromotionPage />} />

        {/* FC퍼널 소개 페이지 */}
        <Route path="/intro" element={<IntroPage />} />

        {/* 업무일지 페이지 */}
        <Route path="/work-diary" element={<WorkDiaryPage />} />

        {/* 고객 소개 관계 트리 페이지 */}
        <Route path="/customer-referral-tree" element={<CustomerReferralTreePage />} />

        {/* 유료전용 페이지 */}

        {/* 공개 법적 문서 */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />

        {/* 관리자 대시보드 - 보호된 라우트 - woogi로 변경 */}
        <Route
          path="/woogi/*"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* 관리자 로그인 페이지 - woogi-login으로 변경 */}
        <Route path="/woogi-login" element={<AdminLogin />} />

        {/* 잘못된 경로는 메인으로 리다이렉트 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </Router>
  );
};

// 관리자 로그인 컴포넌트
const AdminLogin = () => {
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleLogin = async () => {
    setLoading(true);

    try {
      const result = await verifyAdminPassword({ password });
      const ok = !!result?.data?.success;
      if (ok && result?.data?.token) {
        await setPersistence(auth, browserSessionPersistence);
        await signInWithCustomToken(auth, result.data.token);

        // GA4: 관리자 로그인 이벤트
        try {
          const gtag = window.gtag;
          const measurementId = window.GA_MEASUREMENT_ID;
          if (typeof gtag === 'function' && measurementId) {
            const deviceId = generateDeviceId();
            gtag('event', 'login', { 
              method: 'admin_password',
              device_id: deviceId
            });
          }
        } catch (_) {}

        // 관리자 대시보드로 리다이렉트 - /woogi로 변경
        window.location.href = '/woogi';
      } else {
        alert('비밀번호가 틀렸습니다!');
      }
    } catch (e) {
      console.error('관리자 검증 호출 실패:', e);
      alert('잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  };

  // Firebase 세션을 서버가 발급한 관리자 claim으로 검증한다.
  React.useEffect(() => {
    return auth.onAuthStateChanged(async (user) => {
      if (user && (await user.getIdTokenResult()).claims.admin === true) {
        window.location.href = '/woogi';
      }
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-lg">
        <div className="text-center mb-6">
          <div className="text-4xl mb-4">🔐</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">관리자 로그인</h2>
          <p className="text-gray-600">관리자 비밀번호를 입력하세요</p>
        </div>

        <div className="space-y-4">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !loading && handleLogin()}
            className="w-full p-4 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-lg"
            placeholder="관리자 비밀번호"
            autoFocus
            disabled={loading}
          />
          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full bg-blue-500 text-white py-4 rounded-lg font-bold text-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? '로그인 중...' : '로그인'}
          </button>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => window.location.href = '/'}
            className="text-gray-500 hover:text-gray-700"
          >
            ← 메인으로 돌아가기
          </button>
        </div>

        <div className="mt-6 text-center">
          <div className="text-xs text-gray-400">
            영업프로세스를 딴딴하게 | FC퍼널
          </div>
        </div>
      </div>
    </div>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<MainApp />);

// Service Worker 등록은 firebase.js의 getFCMToken()에서 처리됨

export default MainApp;
